import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { DotGrid } from "../atlas-workflow/DotGrid";

type Tab = "routes" | "reach";

/* ── Route animation data ── */
const WAYPOINTS = [
  { x: 100, y: 80 },
  { x: 200, y: 180 },
  { x: 340, y: 120 },
  { x: 460, y: 210 },
  { x: 530, y: 100 },
];

const routePath = WAYPOINTS.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x},${p.y}`).join(" ");

const ROUTE_LABELS = [
  { x: 155, y: 145, text: "12 min" },
  { x: 275, y: 135, text: "8 min" },
  { x: 405, y: 175, text: "11 min" },
  { x: 500, y: 145, text: "5 min" },
];

/* ── Reach animation data ── */
const CENTER = { x: 310, y: 180 };

const ISOCHRONES = [
  {
    label: "10 min",
    color: "hsl(186 100% 18%)",
    opacity: 0.35,
    d: `M ${CENTER.x} ${CENTER.y - 55} C ${CENTER.x + 50} ${CENTER.y - 50}, ${CENTER.x + 65} ${CENTER.y - 10}, ${CENTER.x + 60} ${CENTER.y + 10} C ${CENTER.x + 55} ${CENTER.y + 40}, ${CENTER.x + 30} ${CENTER.y + 55}, ${CENTER.x} ${CENTER.y + 58} C ${CENTER.x - 35} ${CENTER.y + 55}, ${CENTER.x - 60} ${CENTER.y + 30}, ${CENTER.x - 62} ${CENTER.y} C ${CENTER.x - 60} ${CENTER.y - 30}, ${CENTER.x - 45} ${CENTER.y - 52}, ${CENTER.x} ${CENTER.y - 55} Z`,
    delay: 0.3,
  },
  {
    label: "20 min",
    color: "hsl(186 80% 28%)",
    opacity: 0.25,
    d: `M ${CENTER.x + 5} ${CENTER.y - 100} C ${CENTER.x + 80} ${CENTER.y - 90}, ${CENTER.x + 115} ${CENTER.y - 25}, ${CENTER.x + 108} ${CENTER.y + 20} C ${CENTER.x + 100} ${CENTER.y + 70}, ${CENTER.x + 55} ${CENTER.y + 100}, ${CENTER.x - 5} ${CENTER.y + 105} C ${CENTER.x - 60} ${CENTER.y + 100}, ${CENTER.x - 105} ${CENTER.y + 60}, ${CENTER.x - 112} ${CENTER.y} C ${CENTER.x - 108} ${CENTER.y - 50}, ${CENTER.x - 75} ${CENTER.y - 95}, ${CENTER.x + 5} ${CENTER.y - 100} Z`,
    delay: 0.7,
  },
  {
    label: "30 min",
    color: "hsl(186 60% 40%)",
    opacity: 0.15,
    d: `M ${CENTER.x + 10} ${CENTER.y - 145} C ${CENTER.x + 120} ${CENTER.y - 130}, ${CENTER.x + 165} ${CENTER.y - 40}, ${CENTER.x + 155} ${CENTER.y + 30} C ${CENTER.x + 145} ${CENTER.y + 105}, ${CENTER.x + 80} ${CENTER.y + 145}, ${CENTER.x - 10} ${CENTER.y + 150} C ${CENTER.x - 85} ${CENTER.y + 145}, ${CENTER.x - 150} ${CENTER.y + 90}, ${CENTER.x - 160} ${CENTER.y} C ${CENTER.x - 155} ${CENTER.y - 75}, ${CENTER.x - 110} ${CENTER.y - 135}, ${CENTER.x + 10} ${CENTER.y - 145} Z`,
    delay: 1.1,
  },
];

const ISOCHRONE_LABEL_POS = [
  { x: CENTER.x + 62, y: CENTER.y - 15 },
  { x: CENTER.x + 110, y: CENTER.y - 30 },
  { x: CENTER.x + 158, y: CENTER.y - 45 },
];

export function RoutingAnimation() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("routes");

  return (
    <div>
      {/* Tab buttons */}
      <div className="flex justify-center mb-6">
        <div className="inline-flex gap-1 p-1 rounded-xl bg-white/10 border border-white/15">
        {(["routes", "reach"] as Tab[]).map((key) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-6 py-2.5 rounded-lg text-sm font-heading font-semibold transition-all duration-300 ${
              tab === key
                ? "bg-coral text-white shadow-lg shadow-coral/30"
                : "text-primary-foreground/60 hover:text-primary-foreground/90 hover:bg-white/5"
            }`}
          >
            {t(`productPages.atlas.routing.tabs.${key}`)}
          </button>
        ))}
        </div>
      </div>

      {/* SVG canvas */}
      <div className="relative rounded-xl border border-border/40 bg-white/70 dark:bg-card/50 overflow-hidden">
        <AnimatePresence mode="wait">
          {tab === "routes" ? <RouteView key="routes" /> : <ReachView key="reach" />}
        </AnimatePresence>
      </div>
    </div>
  );
}

function RouteView() {
  return (
    <motion.svg
      viewBox="0 0 620 360"
      className="w-full"
      style={{ aspectRatio: "620/360" }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <DotGrid />

      {/* Waypoint markers */}
      {WAYPOINTS.map((p, i) => (
        <motion.circle
          key={i}
          cx={p.x}
          cy={p.y}
          r={6}
          fill="hsl(186 100% 18%)"
          stroke="white"
          strokeWidth={2}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: i * 0.15, type: "spring", stiffness: 300 }}
        />
      ))}

      {/* Animated route path */}
      <motion.path
        d={routePath}
        fill="none"
        stroke="hsl(186 100% 18%)"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 2.5, ease: "easeInOut", delay: 0.5 }}
      />

      {/* Dashed route underlay */}
      <path
        d={routePath}
        fill="none"
        stroke="hsl(186 100% 18% / 0.15)"
        strokeWidth={2}
        strokeDasharray="6 4"
      />

      {/* Time labels */}
      {ROUTE_LABELS.map((lbl, i) => (
        <motion.g
          key={i}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 + i * 0.4 }}
        >
          <rect
            x={lbl.x - 22}
            y={lbl.y - 10}
            width={44}
            height={20}
            rx={4}
            fill="hsl(186 100% 18%)"
            opacity={0.9}
          />
          <text
            x={lbl.x}
            y={lbl.y + 4}
            textAnchor="middle"
            fill="white"
            fontSize={10}
            fontWeight={600}
            fontFamily="var(--font-heading)"
          >
            {lbl.text}
          </text>
        </motion.g>
      ))}

      {/* Moving vehicle dot */}
      <motion.circle
        r={4}
        fill="hsl(var(--coral))"
        initial={{ offsetDistance: "0%" }}
        animate={{ offsetDistance: "100%" }}
        transition={{ duration: 3, ease: "easeInOut", delay: 0.5, repeat: Infinity, repeatDelay: 1.5 }}
        style={{ offsetPath: `path("${routePath}")` } as React.CSSProperties}
      />
    </motion.svg>
  );
}

function ReachView() {
  return (
    <motion.svg
      viewBox="0 0 620 360"
      className="w-full"
      style={{ aspectRatio: "620/360" }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <DotGrid />

      {/* Isochrones (outermost first for layering) */}
      {[...ISOCHRONES].reverse().map((iso, i) => (
        <motion.path
          key={i}
          d={iso.d}
          fill={iso.color}
          fillOpacity={iso.opacity}
          stroke={iso.color}
          strokeWidth={1.5}
          strokeOpacity={0.5}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: iso.delay, duration: 0.8, ease: "easeOut" }}
          style={{ transformOrigin: `${CENTER.x}px ${CENTER.y}px` }}
        />
      ))}

      {/* Labels */}
      {ISOCHRONES.map((iso, i) => (
        <motion.g
          key={i}
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: iso.delay + 0.4 }}
        >
          <rect
            x={ISOCHRONE_LABEL_POS[i].x - 24}
            y={ISOCHRONE_LABEL_POS[i].y - 10}
            width={48}
            height={20}
            rx={4}
            fill={iso.color}
            opacity={0.9}
          />
          <text
            x={ISOCHRONE_LABEL_POS[i].x}
            y={ISOCHRONE_LABEL_POS[i].y + 4}
            textAnchor="middle"
            fill="white"
            fontSize={10}
            fontWeight={600}
            fontFamily="var(--font-heading)"
          >
            {iso.label}
          </text>
        </motion.g>
      ))}

      {/* Center point */}
      <motion.circle
        cx={CENTER.x}
        cy={CENTER.y}
        r={7}
        fill="hsl(186 100% 18%)"
        stroke="white"
        strokeWidth={2.5}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 300 }}
      />
      <motion.circle
        cx={CENTER.x}
        cy={CENTER.y}
        r={14}
        fill="none"
        stroke="hsl(186 100% 18%)"
        strokeWidth={1}
        strokeOpacity={0.4}
        initial={{ scale: 0 }}
        animate={{ scale: [0, 1.4, 1] }}
        transition={{ delay: 0.2, duration: 0.8 }}
      />
    </motion.svg>
  );
}
